/**
 * CrechePage — Module Crèche (Places & CA)
 * 5 onglets : Places, Documents, CA Prévisionnel, Tarifs (lecture seule), Régularisation.
 * Vue multi-structures supportée (ajout inclus).
 * Archivés masqués par défaut avec toggle.
 * Dates début/fin contrat pour CA au prorata.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Baby, TrendingUp, Calendar, Users, Check, X, Plus, Edit2,
  Trash2, CheckCircle, AlertCircle, Building, Loader,
  Settings, Save, Eye, EyeOff, Calculator, Receipt, FileCheck, Minus, Copy,
  Search, ArrowUpDown, ChevronUp, ChevronDown, Download,
} from 'lucide-react';
import {
  getCrecheChildren, createCrecheChild, updateCrecheChild,
  getCrecheContracts, createCrecheContract, updateCrecheContract, deleteCrecheContract,
  getCrecheTariffs, getCrecheCapacity, upsertCrecheCapacity,
  getCrecheMultiStructures,
  getCrecheContractStatuses,
  getFamilyDocuments, getFamilyDocumentsMultiStructures, updateFamilyDocumentCheck,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import RegularizationTab from '../components/RegularizationTab';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Config ───────────────────────────────────────────────────────────────────

const DAYS = [
  { key: 'monday',    short: 'L',   label: 'Lundi' },
  { key: 'tuesday',   short: 'Ma',  label: 'Mardi' },
  { key: 'wednesday', short: 'Me',  label: 'Mercredi' },
  { key: 'thursday',  short: 'J',   label: 'Jeudi' },
  { key: 'friday',    short: 'V',   label: 'Vendredi' },
];

// Fallback hardcodé si la table n'est pas encore seedée
const CONTRACT_STATUS_FALLBACK = {
  prospect:        { label: 'En attente',       color: 'bg-gray-100 text-gray-600' },
  contrat_envoye:  { label: 'Contrat envoyé', color: 'bg-blue-100 text-blue-700' },
  contrat_signe:   { label: 'Contrat signé',  color: 'bg-amber-100 text-amber-700' },
  valide_meeko:    { label: 'Validé Meeko',   color: 'bg-green-100 text-green-700' },
  archive:         { label: 'Archivé',        color: 'bg-red-100 text-red-600' },
};

const DEFAULT_CAPACITY = { monday: 14, tuesday: 14, wednesday: 14, thursday: 14, friday: 14 };

// Mois de l'année scolaire (Sept → Août)
const SCHOOL_MONTHS = [
  { value: 9, label: 'Sept' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Déc' },
  { value: 1, label: 'Jan' },  { value: 2, label: 'Fév' },  { value: 3, label: 'Mars' }, { value: 4, label: 'Avr' },
  { value: 5, label: 'Mai' },  { value: 6, label: 'Juin' }, { value: 7, label: 'Juil' }, { value: 8, label: 'Août' },
];

// ─── Utilitaires ──────────────────────────────────────────────────────────────

const getCurrentSchoolYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 9) return `${year}-${String(year + 1).slice(2)}`;
  return `${year - 1}-${String(year).slice(2)}`;
};

const buildSchoolYearOptions = () => {
  const current = getCurrentSchoolYear();
  const baseYear = parseInt(current.split('-')[0], 10);
  const options = [];
  for (let i = baseYear - 2; i <= baseYear + 1; i++) {
    options.push(`${i}-${String(i + 1).slice(2)}`);
  }
  return options.reverse();
};

const fmtMoney = (v) => (v == null || v === '') ? '—' : `${parseFloat(v).toLocaleString('fr-FR')} €`;

/** Calcule l'âge en "X a Y m" depuis birthDate */
const fmtAge = (birthDate) => {
  if (!birthDate) return '';
  const birth = new Date(birthDate);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (months < 0) { years--; months += 12; }
  if (now.getDate() < birth.getDate()) {
    months--;
    if (months < 0) { years--; months += 12; }
  }
  if (years > 0) return `${years}a ${months}m`;
  return `${months}m`;
};

const getChildName = (child) =>
  child ? [child.firstName, child.lastName].filter(Boolean).join(' ') || '—' : '—';

const fmtTariffLabel = (t) => {
  let label = t.name;
  if (t.isPublicEmployer) label += ' (PE)';
  label += ` — ${fmtMoney(t.monthlyAmount)}`;
  if (t.validFrom || t.validUntil) {
    const from = t.validFrom ? new Date(t.validFrom).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '';
    const until = t.validUntil ? new Date(t.validUntil).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '';
    if (from && until) label += ` [${from} → ${until}]`;
    else if (from) label += ` [dès ${from}]`;
    else if (until) label += ` [jusqu'à ${until}]`;
  }
  return label;
};

/** Filtre les tarifs par nombre de jours, PE, et période de l'année sélectionnée */
const filterTariffs = (allTariffs, daysCount, isPE, schoolYear) => {
  const baseYear = parseInt(schoolYear.split('-')[0], 10);
  const yearStart = new Date(baseYear, 8, 1);
  const yearEnd = new Date(baseYear + 1, 7, 31);
  return allTariffs.filter(t => {
    if (daysCount > 0 && t.days !== daysCount) return false;
    if (t.isPublicEmployer !== isPE) return false;
    if (t.validFrom && new Date(t.validFrom) > yearEnd) return false;
    if (t.validUntil && new Date(t.validUntil) < yearStart) return false;
    return true;
  });
};

/** Retourne les dates ISO par défaut pour l'année scolaire (sept → août) */
const getDefaultContractDates = (schoolYear) => {
  const baseYear = parseInt(schoolYear.split('-')[0], 10);
  return {
    contractStart: new Date(baseYear, 8, 1).toISOString(),     // 1er sept
    contractEnd: new Date(baseYear + 1, 8, 0).toISOString(),   // 31 août (jour 0 de sept = dernier jour août)
  };
};

/** Parse contractStart/End en {month, year} ou retourne null */
const parseMonthDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return { month: dt.getMonth() + 1, year: dt.getFullYear() };
};

/** Format "YYYY-MM" display from a Date */
const fmtMonthShort = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const m = SCHOOL_MONTHS.find(sm => sm.value === dt.getMonth() + 1);
  return m ? `${m.label} ${dt.getFullYear()}` : '';
};

/** Calcule le nombre de mois actifs dans l'année scolaire */
const countActiveMonths = (contractStart, contractEnd, schoolYear) => {
  const baseYear = parseInt(schoolYear.split('-')[0], 10);
  // Mois de l'année scolaire : Sept(9) → Août(8)
  const schoolMonths = [];
  for (let m = 9; m <= 12; m++) schoolMonths.push({ month: m, year: baseYear });
  for (let m = 1; m <= 8; m++) schoolMonths.push({ month: m, year: baseYear + 1 });

  const start = parseMonthDate(contractStart);
  const end = parseMonthDate(contractEnd);

  return schoolMonths.filter(sm => {
    if (start && (sm.year < start.year || (sm.year === start.year && sm.month < start.month))) return false;
    if (end && (sm.year > end.year || (sm.year === end.year && sm.month > end.month))) return false;
    return true;
  }).length;
};

// ─── Badge statut ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, statuses = CONTRACT_STATUS_FALLBACK }) => {
  const cfg = statuses[status] || statuses.prospect || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ─── Barre de capacité ────────────────────────────────────────────────────────

const CapacityBar = ({ count, max = 14 }) => {
  const pct = Math.min((count / max) * 100, 100);
  const isDanger = count > max;
  const isWarning = count >= max;
  const barColor = isDanger ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-amber-400';
  return (
    <div className="flex items-center gap-1">
      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-medium ${isDanger ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-600'}`}>
        {count}/{max}
      </span>
    </div>
  );
};

// ─── Sélecteur de mois (année scolaire) ───────────────────────────────────────

const MonthSelect = ({ value, onChange, schoolYear, placeholder = '—', allowEmpty = true, isEnd = false }) => {
  const parsed = parseMonthDate(value);
  const baseYear = parseInt(schoolYear.split('-')[0], 10);

  const currentVal = parsed
    ? `${parsed.month >= 9 ? baseYear : baseYear + 1}-${String(parsed.month).padStart(2, '0')}`
    : '';

  const handleChange = (e) => {
    const v = e.target.value;
    if (!v) { onChange(null); return; }
    const [y, m] = v.split('-').map(Number);
    // Debut = 1er du mois, Fin = dernier jour du mois
    if (isEnd) {
      onChange(new Date(y, m, 0).toISOString()); // jour 0 du mois suivant = dernier jour du mois
    } else {
      onChange(new Date(y, m - 1, 1).toISOString());
    }
  };

  return (
    <select
      value={currentVal}
      onChange={handleChange}
      className="text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300 bg-white w-full"
    >
      {allowEmpty && <option value="">{placeholder}</option>}
      {SCHOOL_MONTHS.map(sm => {
        const y = sm.value >= 9 ? baseYear : baseYear + 1;
        const val = `${y}-${String(sm.value).padStart(2, '0')}`;
        return <option key={val} value={val}>{sm.label} {y}</option>;
      })}
    </select>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

// Vérifie la permission `creche_previsionnel` (accès au CA prévisionnel + montants négociés PE).
// Admin global toujours autorisé.
const hasCrechePrevisionnelAccess = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const modules = Array.isArray(user.modules) ? user.modules : [];
  return modules.includes('creche_previsionnel');
};

const CrechePage = ({ user, showAllStructures = false, navPayload, onNavPayloadConsumed }) => {
  const canSeePrevisionnel = hasCrechePrevisionnelAccess(user);
  const { currentStructure, structures } = useStructure();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('places');
  const [schoolYear, setSchoolYear] = useState(getCurrentSchoolYear());
  const schoolYearOptions = buildSchoolYearOptions();

  // Données par structure
  const [dataByStructure, setDataByStructure] = useState({});
  const [tariffs, setTariffs] = useState([]);
  const [contractStatuses, setContractStatuses] = useState(CONTRACT_STATUS_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Toggle archivés
  const [showArchived, setShowArchived] = useState(false);



  // Edition ligne existante
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingId, setSavingId] = useState(null);

  // Ajout nouvelle ligne
  const [addingRow, setAddingRow] = useState(null);
  const [newRow, setNewRow] = useState(null);

  // Modal enfant
  const [showChildModal, setShowChildModal] = useState(false);
  const [childForm, setChildForm] = useState({ firstName: '', lastName: '', birthDate: '' });
  const [childStructureId, setChildStructureId] = useState(null);
  const [editingChildId, setEditingChildId] = useState(null);
  const [savingChild, setSavingChild] = useState(false);

  // Capacité
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [capacityForm, setCapacityForm] = useState({});
  const [savingCapacity, setSavingCapacity] = useState(false);

  const isMulti = showAllStructures && structures.length >= 2;
  const activeStructures = isMulti ? structures : (currentStructure ? [currentStructure] : []);

  // ─── Chargement ─────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (activeStructures.length === 0) return;
    setLoading(true);
    setError('');
    try {
      if (isMulti) {
        const ids = structures.map(s => s.id).join(',');
        const res = await getCrecheMultiStructures({ structureIds: ids, schoolYear });
        const data = res.data;
        const dbs = {};
        for (const s of structures) {
          const sd = data.byStructure[s.id] || { contracts: [], capacity: DEFAULT_CAPACITY, occupancy: {}, children: [] };
          dbs[s.id] = {
            contracts: sd.contracts,
            capacity: sd.capacity || DEFAULT_CAPACITY,
            occupancy: sd.occupancy || {},
            children: sd.children || [...new Map(sd.contracts.map(c => [c.childId, c.child]).filter(([, ch]) => ch)).values()],
          };
        }
        setDataByStructure(dbs);
        setTariffs(data.tariffs || []);
      } else {
        const sid = currentStructure.id;
        const [contractRes, tariffRes, capRes, childRes] = await Promise.allSettled([
          getCrecheContracts({ structureId: sid, schoolYear }),
          getCrecheTariffs({ structureId: sid }),
          getCrecheCapacity({ structureId: sid }),
          getCrecheChildren({ structureId: sid }),
        ]);
        const contracts = contractRes.status === 'fulfilled' ? contractRes.value.data : [];
        const cap = capRes.status === 'fulfilled' ? capRes.value.data : DEFAULT_CAPACITY;
        const allChildren = childRes.status === 'fulfilled' ? childRes.value.data : [];
        const occ = {};
        DAYS.forEach(d => {
          occ[d.key] = contracts.filter(c => c.status !== 'archive' && c[d.key]).length;
        });
        setDataByStructure({
          [sid]: { contracts, capacity: cap, occupancy: occ, children: allChildren },
        });
        setTariffs(tariffRes.status === 'fulfilled' ? tariffRes.value.data : []);
      }
    } catch (err) {
      console.error('[CrechePage] fetchAll error:', err);
      toast.error('Erreur lors du chargement des donnees');
      setError('Erreur lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }, [currentStructure, structures, schoolYear, isMulti]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-ouverture fiche enfant depuis la recherche globale (navPayload.childId)
  useEffect(() => {
    if (!navPayload?.childId) return;
    const childId = navPayload.childId;
    // Cherche l'enfant dans toutes les structures chargées
    let found = null;
    let foundStructureId = null;
    Object.entries(dataByStructure || {}).forEach(([sid, sd]) => {
      const ch = (sd.children || []).find(c => c.id === childId);
      if (ch) { found = ch; foundStructureId = sid; }
    });
    if (found) {
      openChildModal(foundStructureId, found);
      onNavPayloadConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navPayload, dataByStructure]);

  // Charger les statuts configurables
  useEffect(() => {
    getCrecheContractStatuses()
      .then(res => {
        if (res.data?.length > 0) {
          const map = {};
          res.data.forEach(s => { map[s.key] = { label: s.label, color: s.color }; });
          setContractStatuses(map);
        }
      })
      .catch(() => {}); // fallback hardcodé
  }, []);

  // ─── Édition ligne existante ─────────────────────────────────────────────────

  const startEdit = (contract) => {
    setEditingId(contract.id);
    setEditForm({
      monday: !!contract.monday, tuesday: !!contract.tuesday, wednesday: !!contract.wednesday,
      thursday: !!contract.thursday, friday: !!contract.friday,
      tariffId: String(contract.tariffId || ''),
      isPublicEmployer: !!contract.isPublicEmployer,
      status: contract.status || 'prospect',
      notes: contract.notes || '',
      contractStart: contract.contractStart || null,
      contractEnd: contract.contractEnd || null,
      // negotiatedAmount peut être absent de la réponse API si l'utilisateur n'a pas creche_previsionnel
      // (le champ n'est alors ni éditable ni envoyé au backend)
      negotiatedAmount: contract.negotiatedAmount != null ? String(contract.negotiatedAmount) : '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(null); };

  const handleSaveEdit = async () => {
    if (!editingId || !editForm) return;
    setSavingId(editingId);
    try {
      await updateCrecheContract(editingId, {
        monday: !!editForm.monday, tuesday: !!editForm.tuesday, wednesday: !!editForm.wednesday,
        thursday: !!editForm.thursday, friday: !!editForm.friday,
        tariffId: editForm.tariffId ? parseInt(editForm.tariffId, 10) : null,
        isPublicEmployer: !!editForm.isPublicEmployer,
        status: editForm.status,
        notes: editForm.notes || null,
        contractStart: editForm.contractStart || null,
        contractEnd: editForm.contractEnd || null,
        // Champ ignoré silencieusement par le backend si l'utilisateur n'a pas creche_previsionnel
        negotiatedAmount: editForm.negotiatedAmount === '' ? null : editForm.negotiatedAmount,
      });
      cancelEdit();
      await fetchAll();
      toast.success('Contrat modifie');
    } catch (err) {
      console.error('[CrechePage] saveEdit error:', err);
      toast.error('Erreur lors de la modification du contrat');
    } finally {
      setSavingId(null);
    }
  };

  // ─── Ajout nouvelle ligne ──────────────────────────────────────────────────

  const startAddRow = (structureId) => {
    setAddingRow(structureId);
    const defaults = getDefaultContractDates(schoolYear);
    setNewRow({
      childId: '', tariffId: '', isPublicEmployer: false,
      monday: false, tuesday: false, wednesday: false, thursday: false, friday: false,
      status: 'prospect', notes: '',
      contractStart: defaults.contractStart, contractEnd: defaults.contractEnd,
      negotiatedAmount: '',
    });
  };

  const cancelAddRow = () => { setAddingRow(null); setNewRow(null); };

  const handleSaveNewRow = async (structureId) => {
    if (!newRow.childId) return;
    setSavingId('new');
    try {
      await createCrecheContract({
        ...newRow, structureId, schoolYear,
        tariffId: newRow.tariffId ? parseInt(newRow.tariffId, 10) : null,
        // Backend ignore silencieusement negotiatedAmount si l'utilisateur n'a pas creche_previsionnel
        negotiatedAmount: newRow.negotiatedAmount === '' ? null : newRow.negotiatedAmount,
      });
      cancelAddRow();
      await fetchAll();
      toast.success('Contrat ajoute');
    } catch (err) {
      console.error('[CrechePage] saveNewRow error:', err);
      toast.error('Erreur lors de la creation du contrat');
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      await deleteCrecheContract(id);
      await fetchAll();
      toast.success('Contrat supprime');
    } catch (err) {
      console.error('[CrechePage] deleteContract error:', err);
      toast.error('Erreur lors de la suppression du contrat');
    }
  };

  // ─── CRUD enfant ──────────────────────────────────────────────────────────────

  const openChildModal = (structureId, child = null) => {
    setChildStructureId(structureId);
    setEditingChildId(child ? child.id : null);
    setChildForm({
      firstName: child?.firstName || '',
      lastName: child?.lastName || '',
      birthDate: child?.birthDate ? child.birthDate.slice(0, 10) : '',
    });
    setShowChildModal(true);
  };

  const handleSaveChild = async () => {
    if (!childForm.firstName.trim()) return;
    setSavingChild(true);
    try {
      if (editingChildId) {
        await updateCrecheChild(editingChildId, childForm);
        toast.success('Enfant mis a jour');
      } else {
        const res = await createCrecheChild({ ...childForm, structureId: childStructureId });
        if (addingRow === childStructureId && newRow) {
          setNewRow(prev => ({ ...prev, childId: String(res.data.id) }));
        }
        toast.success('Enfant ajoute');
      }
      setShowChildModal(false);
      await fetchAll();
    } catch (err) {
      console.error('[CrechePage] saveChild error:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingChild(false);
    }
  };

  // ─── Capacité ─────────────────────────────────────────────────────────────────

  const startEditCapacity = (structureId, currentCap) => {
    setEditingCapacity(structureId);
    setCapacityForm({ ...currentCap });
  };

  const handleSaveCapacity = async () => {
    setSavingCapacity(true);
    try {
      await upsertCrecheCapacity({ structureId: editingCapacity, ...capacityForm });
      setEditingCapacity(null);
      await fetchAll();
      toast.success('Capacite mise a jour');
    } catch (err) {
      console.error('[CrechePage] saveCapacity error:', err);
      toast.error('Erreur lors de la mise a jour de la capacite');
    } finally {
      setSavingCapacity(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (!currentStructure && !isMulti) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Building size={32} className="mx-auto mb-2 opacity-40" />
        <p>Sélectionnez une structure pour accéder au module Crèche.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Baby size={20} className="text-amber-500" />
            <h1 className="text-lg font-semibold text-gray-800">
              Crèche — Places & CA
              {isMulti && <span className="text-sm font-normal text-gray-400 ml-2">(Toutes structures)</span>}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle archivés */}
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                showArchived ? 'bg-red-50 text-red-600 border border-red-200' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={showArchived ? 'Masquer les archivés' : 'Afficher les archivés'}
            >
              {showArchived ? <Eye size={13} /> : <EyeOff size={13} />}
              <span className="hidden sm:inline">Archivés</span>
            </button>

            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-gray-400" />
              <label className="text-xs text-gray-500">Année</label>
              <select
                value={schoolYear}
                onChange={e => setSchoolYear(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-300"
              >
                {schoolYearOptions.map(y => (
                  <option key={y} value={y}>{y.replace('-', '/20')}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-1 mt-3">
          {[
            { id: 'places',       label: 'Places',          Icon: Users },
            { id: 'documents',    label: 'Suivi docs',      Icon: FileCheck },
            // Onglet CA Prévisionnel : visible uniquement avec la permission `creche_previsionnel`
            ...(canSeePrevisionnel ? [{ id: 'previsionnel', label: 'CA Prévisionnel', Icon: TrendingUp }] : []),
            { id: 'tariffs',      label: 'Tarifs',          Icon: Receipt },
            { id: 'regularization', label: 'Régularisation', Icon: Calculator },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === id ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <LoadingSpinner size="default" text="Chargement..." inline />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        ) : (
          <>
            {activeTab === 'places' && (
              <div className="space-y-6">
                {activeStructures.map(structure => (
                  <StructurePlacesTable
                    key={structure.id}
                    structure={structure}
                    data={dataByStructure[structure.id] || { contracts: [], capacity: DEFAULT_CAPACITY, occupancy: {}, children: [] }}
                    tariffs={tariffs}
                    schoolYear={schoolYear}
                    showStructureName={isMulti}
                    showArchived={showArchived}
                    editingId={editingId}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onSaveEdit={handleSaveEdit}
                    addingRow={addingRow === structure.id}
                    newRow={newRow}
                    setNewRow={setNewRow}
                    onStartAdd={() => startAddRow(structure.id)}
                    onCancelAdd={cancelAddRow}
                    onSaveNew={() => handleSaveNewRow(structure.id)}
                    onDelete={handleDeleteContract}
                    onAddChild={() => openChildModal(structure.id)}
                    onEditChild={openChildModal}
                    savingId={savingId}
                    editingCapacity={editingCapacity === structure.id}
                    capacityForm={capacityForm}
                    setCapacityForm={setCapacityForm}
                    onStartEditCapacity={() => startEditCapacity(structure.id, dataByStructure[structure.id]?.capacity || DEFAULT_CAPACITY)}
                    onCancelEditCapacity={() => setEditingCapacity(null)}
                    onSaveCapacity={handleSaveCapacity}
                    savingCapacity={savingCapacity}
                    contractStatuses={contractStatuses}
                    canSeePrevisionnel={canSeePrevisionnel}
                  />
                ))}
              </div>
            )}
            {activeTab === 'documents' && (
              <DocumentsTab
                structures={activeStructures}
                showStructureName={isMulti}
                schoolYear={schoolYear}
                showArchived={showArchived}
              />
            )}
            {activeTab === 'previsionnel' && (
              <PrevisionnelTab
                dataByStructure={dataByStructure}
                structures={activeStructures}
                showStructureName={isMulti}
                schoolYear={schoolYear}
                showArchived={showArchived}
                contractStatuses={contractStatuses}
              />
            )}
            {activeTab === 'tariffs' && (
              <TariffsReadOnlyTab
                tariffs={tariffs}
                structures={activeStructures}
                showStructureName={isMulti}
                schoolYear={schoolYear}
              />
            )}
            {activeTab === 'regularization' && (
              <RegularizationTab
                structureId={currentStructure?.id}
                structures={activeStructures}
                showAllStructures={isMulti}
                schoolYear={schoolYear}
              />
            )}
          </>
        )}
      </div>

      {/* Modal nouvel enfant */}
      {showChildModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Baby size={16} className="text-amber-500" />
                <h2 className="font-semibold text-gray-800">{editingChildId ? 'Modifier l\'enfant' : 'Nouvel enfant'}</h2>
              </div>
              <button onClick={() => setShowChildModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Prénom *</label>
                  <input type="text" value={childForm.firstName}
                    onChange={e => setChildForm(p => ({ ...p, firstName: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    placeholder="Léa" autoFocus />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Nom</label>
                  <input type="text" value={childForm.lastName}
                    onChange={e => setChildForm(p => ({ ...p, lastName: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    placeholder="Martin" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date de naissance</label>
                <input type="date" value={childForm.birthDate}
                  onChange={e => setChildForm(p => ({ ...p, birthDate: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowChildModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium">Annuler</button>
              <button onClick={handleSaveChild}
                disabled={savingChild || !childForm.firstName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {savingChild ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                {editingChildId ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tableau Places par structure ────────────────────────────────────────────

// ─── Vue mensuelle : évolution des places sur l'année scolaire ────────────────
// Matrice mois × jours de la semaine. Chaque cellule = `nb enfants présents / capacité`.
// Colonnes annexes : arrivées et départs du mois.
// NB : la capacité par jour est aujourd'hui fixe (CrecheCapacity, 1 valeur permanente par jour).
// TODO : gérer une capacité variable dans le temps quand la nouvelle organisation le nécessitera.
const MONTH_ABBR_FR = ['', 'janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
const DAY_FIELDS_MONTHLY = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_LABELS_SHORT = { monday: 'Lu', tuesday: 'Ma', wednesday: 'Me', thursday: 'Je', friday: 'Ve' };

const MonthlyPlacesEvolution = ({ contracts = [], capacity, schoolYear }) => {
  const [expanded, setExpanded] = useState(false);
  if (!schoolYear) return null;
  const baseYear = parseInt(schoolYear.split('-')[0], 10);
  const months = [];
  for (let m = 9; m <= 12; m++) months.push({ m, y: baseYear });
  for (let m = 1; m <= 8; m++) months.push({ m, y: baseYear + 1 });

  const activeContracts = contracts.filter(c => c.status !== 'archive');
  const cap = capacity || { monday: 14, tuesday: 14, wednesday: 14, thursday: 14, friday: 14 };

  const rows = months.map(({ m, y }) => {
    // Un contrat est actif pour le mois M s'il chevauche le mois (au moins 1 jour couvert).
    // Ancienne logique (midMonth 15) : buggée pour les contrats démarrant en fin de mois
    // (ex: 31/08 rentrée) → étaient exclus alors qu'ils couvrent bien tout le mois.
    const monthStart = new Date(y, m - 1, 1);          // 1er du mois à 00:00
    const monthEnd = new Date(y, m, 0, 23, 59, 59);    // dernier jour du mois à 23:59:59
    const active = activeContracts.filter(c => {
      const start = c.contractStart ? new Date(c.contractStart) : null;
      const end = c.contractEnd ? new Date(c.contractEnd) : null;
      if (start && start > monthEnd) return false;     // commence après la fin du mois
      if (end && end < monthStart) return false;       // finit avant le début du mois
      return true;
    });
    // Nombre d'enfants présents par jour de la semaine
    const perDay = {};
    for (const d of DAY_FIELDS_MONTHLY) {
      perDay[d] = active.filter(c => c[d]).length;
    }
    const arrivals = activeContracts.filter(c => {
      if (!c.contractStart) return false;
      const s = new Date(c.contractStart);
      return s.getMonth() + 1 === m && s.getFullYear() === y;
    }).length;
    const departures = activeContracts.filter(c => {
      if (!c.contractEnd) return false;
      const e = new Date(c.contractEnd);
      return e.getMonth() + 1 === m && e.getFullYear() === y;
    }).length;
    return { m, y, perDay, arrivals, departures };
  });

  // Classe de couleur selon le remplissage (par cellule jour)
  const cellClass = (occupied, capDay) => {
    if (capDay <= 0) return 'text-gray-300';
    const ratio = occupied / capDay;
    if (ratio >= 1) return 'bg-red-50 text-red-700 font-semibold';
    if (ratio >= 0.9) return 'bg-amber-50 text-amber-700 font-medium';
    if (ratio > 0) return 'bg-emerald-50 text-emerald-700';
    return 'text-gray-400';
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(x => !x)}
        className="w-full px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
      >
        <span>Évolution mensuelle des places — {schoolYear} <span className="ml-2 text-[10px] text-gray-400 font-normal">(nb enfants présents / capacité)</span></span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 border-b border-gray-100">
                <th className="text-left px-2 py-1.5">Mois</th>
                {DAY_FIELDS_MONTHLY.map(d => (
                  <th key={d} className="text-center px-2 py-1.5" title={`Capacité ${DAY_LABELS_SHORT[d]} : ${cap[d]}`}>
                    {DAY_LABELS_SHORT[d]}
                  </th>
                ))}
                <th className="text-center px-2 py-1.5 border-l border-gray-200" title="Nouveaux contrats démarrant ce mois">Arrivées</th>
                <th className="text-center px-2 py-1.5" title="Contrats se terminant ce mois">Départs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ m, y, perDay, arrivals, departures }) => (
                <tr key={`${y}-${m}`} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-2 py-1.5 font-medium text-gray-700 whitespace-nowrap">{MONTH_ABBR_FR[m]} {y}</td>
                  {DAY_FIELDS_MONTHLY.map(d => (
                    <td key={d} className={`text-center px-2 py-1.5 ${cellClass(perDay[d], cap[d])}`}>
                      {perDay[d]}/{cap[d]}
                    </td>
                  ))}
                  <td className="text-center px-2 py-1.5 text-emerald-600 font-medium border-l border-gray-200">
                    {arrivals > 0 ? `+${arrivals}` : ''}
                  </td>
                  <td className="text-center px-2 py-1.5 text-red-500 font-medium">
                    {departures > 0 ? `-${departures}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StructurePlacesTable = ({
  structure, data, tariffs, schoolYear, showStructureName, showArchived,
  editingId, editForm, setEditForm, onStartEdit, onCancelEdit, onSaveEdit,
  addingRow, newRow, setNewRow, onStartAdd, onCancelAdd, onSaveNew,
  onDelete, onAddChild, onEditChild, savingId,
  editingCapacity, capacityForm, setCapacityForm,
  onStartEditCapacity, onCancelEditCapacity, onSaveCapacity, savingCapacity,
  contractStatuses = CONTRACT_STATUS_FALLBACK,
  canSeePrevisionnel = false,
}) => {
  const { contracts, capacity, occupancy, children } = data;
  const archivedCount = contracts.filter(c => c.status === 'archive').length;
  const allChildren = children || [];

  // ── Filtres & tri ──
  const [searchText, setSearchText] = useState('');
  const [filterTariffId, setFilterTariffId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortField, setSortField] = useState('name'); // name | age | status | tariff | days
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={11} className="text-gray-300 ml-0.5" />;
    return sortDir === 'asc' ? <ChevronUp size={11} className="text-amber-600 ml-0.5" /> : <ChevronDown size={11} className="text-amber-600 ml-0.5" />;
  };

  // Filtrage
  let visibleContracts = showArchived ? [...contracts] : contracts.filter(c => c.status !== 'archive');
  if (searchText.trim()) {
    const q = searchText.toLowerCase();
    visibleContracts = visibleContracts.filter(c => {
      const name = `${c.child?.firstName || ''} ${c.child?.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }
  if (filterTariffId) visibleContracts = visibleContracts.filter(c => c.tariff?.name === filterTariffId);
  if (filterStatus) visibleContracts = visibleContracts.filter(c => c.status === filterStatus);

  // Tri
  visibleContracts.sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'name': {
        const na = `${a.child?.lastName || ''} ${a.child?.firstName || ''}`.toLowerCase();
        const nb = `${b.child?.lastName || ''} ${b.child?.firstName || ''}`.toLowerCase();
        cmp = na.localeCompare(nb);
        break;
      }
      case 'age': {
        const da = a.child?.birthDate ? new Date(a.child.birthDate).getTime() : 0;
        const db = b.child?.birthDate ? new Date(b.child.birthDate).getTime() : 0;
        cmp = db - da; // plus jeune = date plus récente = plus grand timestamp → asc = plus jeune en premier
        break;
      }
      case 'status': {
        const order = ['prospect', 'contrat_envoye', 'contrat_signe', 'valide_meeko', 'archive'];
        cmp = order.indexOf(a.status) - order.indexOf(b.status);
        break;
      }
      case 'tariff': {
        const ta = a.tariff?.name || '';
        const tb = b.tariff?.name || '';
        cmp = ta.localeCompare(tb);
        break;
      }
      case 'days': {
        const da = DAYS.filter(d => a[d.key]).length;
        const db = DAYS.filter(d => b[d.key]).length;
        cmp = da - db;
        break;
      }
      default: cmp = 0;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Tarifs filtrés pour la nouvelle ligne
  const newRowDays = newRow ? DAYS.filter(d => newRow[d.key]).length : 0;
  const filteredNewTariffs = newRow ? filterTariffs(tariffs, newRowDays, !!newRow.isPublicEmployer, schoolYear) : [];

  // Tarifs filtrés pour la ligne en édition
  const editDays = editForm ? DAYS.filter(d => editForm[d.key]).length : 0;
  const filteredEditTariffs = editForm ? filterTariffs(tariffs, editDays, !!editForm.isPublicEmployer, schoolYear) : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {showStructureName && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <Building size={14} className="text-amber-600" />
          <h2 className="font-semibold text-amber-800 text-sm">{structure.name}</h2>
          <span className="text-xs text-amber-600">
            {contracts.filter(c => c.status !== 'archive').length} place(s)
            {archivedCount > 0 && !showArchived && <span className="text-gray-400 ml-1">+ {archivedCount} archivé(s)</span>}
          </span>
        </div>
      )}

      {/* Barre de filtres */}
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3 flex-wrap bg-gray-50/50">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Rechercher un enfant..."
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-48 focus:outline-none focus:ring-1 focus:ring-amber-300"
          />
        </div>
        <select value={filterTariffId} onChange={e => setFilterTariffId(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Tous les tarifs</option>
          {[...new Map(tariffs.filter(t => !t.structureId || t.structureId === structure.id).map(t => [t.name, t])).values()].map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Tous les statuts</option>
          {Object.entries(contractStatuses).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        {(searchText || filterTariffId || filterStatus) && (
          <button onClick={() => { setSearchText(''); setFilterTariffId(''); setFilterStatus(''); }}
            className="text-xs text-gray-400 hover:text-gray-600">✕ Réinitialiser</button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{visibleContracts.length} contrat(s)</span>
      </div>

      <div className="overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-amber-100">
              <th className="text-left px-3 py-2.5 font-semibold text-gray-700 min-w-[140px] bg-amber-50 cursor-pointer select-none" onClick={() => handleSort('name')}>
                <span className="flex items-center">Enfant <SortIcon field="name" /></span>
              </th>
              <th className="text-center px-2 py-2.5 font-semibold text-gray-700 w-16 bg-amber-50 cursor-pointer select-none" onClick={() => handleSort('age')}>
                <span className="flex items-center justify-center">Âge <SortIcon field="age" /></span>
              </th>
              {DAYS.map(d => (
                <th key={d.key} className="text-center px-1 py-2.5 font-semibold text-gray-700 w-10 bg-amber-50">
                  <span>{d.short}</span>
                </th>
              ))}
              <th className="text-center px-1 py-2.5 font-semibold text-gray-700 w-8 bg-amber-50 cursor-pointer select-none" onClick={() => handleSort('days')}>
                <span className="flex items-center justify-center">J. <SortIcon field="days" /></span>
              </th>
              <th className="text-left px-2 py-2.5 font-semibold text-gray-700 min-w-[140px] bg-amber-50 cursor-pointer select-none" onClick={() => handleSort('tariff')}>
                <span className="flex items-center">Tarif <SortIcon field="tariff" /></span>
              </th>
              <th className="text-center px-1 py-2.5 font-semibold text-gray-700 w-8 bg-amber-50" title="Place entreprise">PE</th>
              <th className="text-center px-2 py-2.5 font-semibold text-gray-700 w-20 bg-amber-50">Début</th>
              <th className="text-center px-2 py-2.5 font-semibold text-gray-700 w-20 bg-amber-50">Fin</th>
              <th className="text-right px-2 py-2.5 font-semibold text-gray-700 w-20 bg-amber-50">€/mois</th>
              {canSeePrevisionnel && (
                <th className="text-right px-2 py-2.5 font-semibold text-gray-700 w-24 bg-amber-50" title="Montant négocié Place Entreprise (confidentiel)">€ PE nég.</th>
              )}
              <th className="text-center px-2 py-2.5 font-semibold text-gray-700 w-28 bg-amber-50 cursor-pointer select-none" onClick={() => handleSort('status')}>
                <span className="flex items-center justify-center">Statut <SortIcon field="status" /></span>
              </th>
              <th className="text-left px-2 py-2.5 font-semibold text-gray-700 min-w-[100px] bg-amber-50">Notes</th>
              <th className="text-center px-1 py-2.5 font-semibold text-gray-700 w-20 bg-amber-50"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleContracts.map(contract => {
              const isEditing = editingId === contract.id;
              const isSaving = savingId === contract.id;
              const form = isEditing ? editForm : null;
              const days = isEditing
                ? DAYS.filter(d => form[d.key]).length
                : DAYS.filter(d => contract[d.key]).length;
              const tarifAmount = contract.tariff ? parseFloat(contract.tariff.monthlyAmount || 0) : 0;
              const isArchived = contract.status === 'archive';

              return (
                <tr key={contract.id} className={`transition-colors ${isArchived ? 'opacity-50 bg-gray-50' : ''} ${isEditing ? 'bg-amber-50/30' : 'hover:bg-gray-50'} ${isSaving ? 'opacity-60' : ''}`}>
                  {/* Enfant */}
                  <td className="px-3 py-2 font-medium text-gray-800 text-xs">
                    {contract.child ? (
                      <button
                        onClick={() => onEditChild(contract.structureId, contract.child)}
                        className="hover:text-amber-600 hover:underline cursor-pointer text-left"
                        title="Modifier l'enfant"
                      >
                        {getChildName(contract.child)}
                      </button>
                    ) : '—'}
                  </td>
                  {/* Âge */}
                  <td className="text-center px-2 py-2 text-xs text-gray-500">
                    {contract.child ? fmtAge(contract.child.birthDate) : ''}
                  </td>

                  {/* Jours */}
                  {DAYS.map(d => (
                    <td key={d.key} className="text-center px-1 py-2">
                      {isEditing ? (
                        <button
                          onClick={() => setEditForm(p => ({ ...p, [d.key]: !p[d.key] }))}
                          className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center transition-colors ${
                            form[d.key] ? 'bg-amber-400 hover:bg-amber-500' : 'bg-gray-100 hover:bg-amber-200'
                          }`}
                        >
                          {form[d.key] && <Check size={12} className="text-white" />}
                        </button>
                      ) : (
                        contract[d.key] ? (
                          <div className="w-6 h-6 rounded-full bg-amber-400 mx-auto flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-100 mx-auto" />
                        )
                      )}
                    </td>
                  ))}

                  <td className="text-center px-1 py-2 font-medium text-amber-700 text-xs">{days}j</td>

                  {/* Tarif */}
                  <td className="px-2 py-2">
                    {isEditing ? (
                      <select
                        value={form.tariffId}
                        onChange={e => setEditForm(p => ({ ...p, tariffId: e.target.value }))}
                        className="w-full text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300 bg-white"
                      >
                        <option value="">—</option>
                        {filteredEditTariffs.map(t => (
                          <option key={t.id} value={String(t.id)}>{fmtTariffLabel(t)}</option>
                        ))}
                        {form.tariffId && !filteredEditTariffs.find(t => String(t.id) === form.tariffId) && (
                          <option value={form.tariffId} disabled>
                            {tariffs.find(t => String(t.id) === form.tariffId)?.name || `Tarif #${form.tariffId}`} (hors filtre)
                          </option>
                        )}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-600">{contract.tariff?.name || '—'}</span>
                    )}
                  </td>

                  {/* PE */}
                  <td className="text-center px-1 py-2">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={!!form.isPublicEmployer}
                        onChange={e => setEditForm(p => ({ ...p, isPublicEmployer: e.target.checked, tariffId: '' }))}
                        className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                      />
                    ) : (
                      contract.isPublicEmployer ? <Check size={14} className="mx-auto text-amber-600" /> : null
                    )}
                  </td>

                  {/* Début */}
                  <td className="text-center px-2 py-2">
                    {isEditing ? (
                      <MonthSelect
                        value={form.contractStart}
                        onChange={v => setEditForm(p => ({ ...p, contractStart: v }))}
                        schoolYear={schoolYear}
                        placeholder="Sept"
                      />
                    ) : (
                      <span className="text-xs text-gray-500">{fmtMonthShort(contract.contractStart) || '—'}</span>
                    )}
                  </td>

                  {/* Fin */}
                  <td className="text-center px-2 py-2">
                    {isEditing ? (
                      <MonthSelect
                        value={form.contractEnd}
                        onChange={v => setEditForm(p => ({ ...p, contractEnd: v }))}
                        schoolYear={schoolYear}
                        placeholder="Août"
                        isEnd
                      />
                    ) : (
                      <span className="text-xs text-gray-500">{fmtMonthShort(contract.contractEnd) || '—'}</span>
                    )}
                  </td>

                  {/* Montant */}
                  <td className="text-right px-2 py-2 font-medium text-gray-700 text-xs">
                    {tarifAmount > 0 ? fmtMoney(tarifAmount) : '—'}
                  </td>

                  {/* Montant PE négocié (visible uniquement avec creche_previsionnel) */}
                  {canSeePrevisionnel && (
                    <td className="text-right px-2 py-2 text-xs">
                      {isEditing && form.isPublicEmployer ? (
                        <input
                          type="number" min="0" step="0.01"
                          value={form.negotiatedAmount ?? ''}
                          onChange={e => setEditForm(p => ({ ...p, negotiatedAmount: e.target.value }))}
                          placeholder="—"
                          className="w-20 text-right text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300"
                          title="Montant négocié Place Entreprise (mensuel)"
                        />
                      ) : contract.isPublicEmployer ? (
                        contract.negotiatedAmount != null
                          ? <span className="font-semibold text-amber-700">{fmtMoney(parseFloat(contract.negotiatedAmount))}</span>
                          : <span className="text-gray-300 italic" title="Non renseigné">—</span>
                      ) : (
                        <span className="text-gray-200">—</span>
                      )}
                    </td>
                  )}

                  {/* Statut */}
                  <td className="text-center px-2 py-2">
                    {isEditing ? (
                      <select
                        value={form.status}
                        onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}
                        className="text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300 bg-white"
                      >
                        {Object.entries(contractStatuses).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    ) : (
                      <StatusBadge status={contract.status} statuses={contractStatuses} />
                    )}
                  </td>

                  {/* Notes */}
                  <td className="px-2 py-2">
                    {isEditing ? (
                      <textarea
                        value={form.notes}
                        onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                        placeholder="Notes..."
                        rows={2}
                        className="w-full min-w-[160px] text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300 resize-y"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 block max-w-[200px] whitespace-pre-wrap" title={contract.notes || ''}>
                        {contract.notes || ''}
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="text-center px-1 py-2">
                    {isEditing ? (
                      <div className="flex items-center gap-1 justify-center">
                        <button onClick={onSaveEdit} disabled={isSaving}
                          className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50" title="Enregistrer">
                          {isSaving ? <Loader size={13} className="animate-spin" /> : <Check size={13} />}
                        </button>
                        <button onClick={onCancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Annuler">
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 justify-center">
                        <button onClick={() => onStartEdit(contract)}
                          className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Modifier">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => onDelete(contract.id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Supprimer">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Ligne d'ajout */}
            {addingRow && (
              <tr className="bg-amber-50/40">
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <select value={newRow.childId}
                      onChange={e => setNewRow(p => ({ ...p, childId: e.target.value }))}
                      className="flex-1 text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300">
                      <option value="">— Enfant —</option>
                      {allChildren.map(ch => (
                        <option key={ch.id} value={String(ch.id)}>{getChildName(ch)}</option>
                      ))}
                    </select>
                    <button onClick={onAddChild} className="p-1 text-amber-600 hover:bg-amber-100 rounded" title="Nouvel enfant">
                      <Plus size={13} />
                    </button>
                  </div>
                </td>
                <td className="px-2 py-2 text-xs text-gray-400 text-center">
                  {newRow.childId ? fmtAge(allChildren.find(c => String(c.id) === newRow.childId)?.birthDate) : ''}
                </td>
                {DAYS.map(d => (
                  <td key={d.key} className="text-center px-1 py-2">
                    <button
                      onClick={() => setNewRow(p => ({ ...p, [d.key]: !p[d.key] }))}
                      className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center transition-colors ${
                        newRow[d.key] ? 'bg-amber-400 hover:bg-amber-500' : 'bg-gray-100 hover:bg-amber-200'
                      }`}>
                      {newRow[d.key] && <Check size={12} className="text-white" />}
                    </button>
                  </td>
                ))}
                <td className="text-center px-1 py-2 font-medium text-amber-700 text-xs">{newRowDays}j</td>
                <td className="px-2 py-2">
                  <select value={newRow.tariffId}
                    onChange={e => setNewRow(p => ({ ...p, tariffId: e.target.value }))}
                    className="w-full text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300">
                    <option value="">—</option>
                    {filteredNewTariffs.map(t => (
                      <option key={t.id} value={String(t.id)}>{fmtTariffLabel(t)}</option>
                    ))}
                  </select>
                </td>
                <td className="text-center px-1 py-2">
                  <input type="checkbox" checked={!!newRow.isPublicEmployer}
                    onChange={e => setNewRow(p => ({ ...p, isPublicEmployer: e.target.checked, tariffId: '' }))}
                    className="rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                </td>
                <td className="px-2 py-2">
                  <MonthSelect value={newRow.contractStart}
                    onChange={v => setNewRow(p => ({ ...p, contractStart: v }))}
                    schoolYear={schoolYear} placeholder="Sept" />
                </td>
                <td className="px-2 py-2">
                  <MonthSelect value={newRow.contractEnd}
                    onChange={v => setNewRow(p => ({ ...p, contractEnd: v }))}
                    schoolYear={schoolYear} placeholder="Août" isEnd />
                </td>
                <td className="px-2 py-2 text-right text-xs text-gray-400">—</td>
                {canSeePrevisionnel && (
                  <td className="text-right px-2 py-2 text-xs">
                    {newRow.isPublicEmployer ? (
                      <input
                        type="number" min="0" step="0.01"
                        value={newRow.negotiatedAmount ?? ''}
                        onChange={e => setNewRow(p => ({ ...p, negotiatedAmount: e.target.value }))}
                        placeholder="Annuel €"
                        className="w-20 text-right text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300"
                        title="Montant annuel négocié Place Entreprise"
                      />
                    ) : <span className="text-gray-200">—</span>}
                  </td>
                )}
                <td className="px-2 py-2">
                  <select value={newRow.status}
                    onChange={e => setNewRow(p => ({ ...p, status: e.target.value }))}
                    className="text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300">
                    {Object.entries(contractStatuses).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-2 py-2">
                  <textarea value={newRow.notes}
                    onChange={e => setNewRow(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Notes..."
                    rows={2}
                    className="w-full min-w-[160px] text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-300 resize-y" />
                </td>
                <td className="text-center px-1 py-2">
                  <div className="flex items-center gap-1 justify-center">
                    <button onClick={onSaveNew} disabled={savingId === 'new' || !newRow.childId}
                      className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50" title="Enregistrer">
                      {savingId === 'new' ? <Loader size={13} className="animate-spin" /> : <Check size={13} />}
                    </button>
                    <button onClick={onCancelAdd} className="p-1 text-gray-400 hover:bg-gray-100 rounded" title="Annuler">
                      <X size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-200">
              <td className="px-3 py-2.5 text-xs font-semibold text-gray-600" colSpan={2}>Places occupées</td>
              {DAYS.map(d => {
                const count = occupancy[d.key] || 0;
                const max = capacity[d.key] || 14;
                const isDanger = count > max;
                const isWarning = count >= max;
                return (
                  <td key={d.key} className="text-center px-1 py-2.5">
                    <span className={`font-semibold text-sm ${isDanger ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-700'}`}>
                      {count}
                    </span>
                    {isDanger && <AlertCircle size={10} className="inline ml-0.5 text-red-500" />}
                  </td>
                );
              })}
              <td colSpan={canSeePrevisionnel ? 9 : 8} />
            </tr>
            <tr className="bg-gray-50 border-t border-gray-100">
              <td className="px-3 py-2 text-xs font-medium text-green-700" colSpan={2}>Places disponibles</td>
              {DAYS.map(d => {
                const count = occupancy[d.key] || 0;
                const max = capacity[d.key] || 14;
                const dispo = max - count;
                return (
                  <td key={d.key} className="text-center px-1 py-2">
                    <span className={`font-semibold text-sm ${dispo <= 0 ? 'text-red-600' : dispo <= 2 ? 'text-amber-600' : 'text-green-600'}`}>
                      {dispo}
                    </span>
                  </td>
                );
              })}
              <td colSpan={canSeePrevisionnel ? 9 : 8} />
            </tr>
            <tr className="bg-gray-50 border-t border-gray-100">
              <td className="px-3 py-2 text-xs text-gray-400" colSpan={2}>
                <span className="flex items-center gap-1">
                  Capacité max
                  {!editingCapacity && (
                    <button onClick={onStartEditCapacity} className="p-0.5 text-gray-400 hover:text-amber-600 rounded" title="Modifier la capacité">
                      <Settings size={11} />
                    </button>
                  )}
                  {editingCapacity && (
                    <>
                      <button onClick={onSaveCapacity} disabled={savingCapacity} className="p-0.5 text-green-600 hover:bg-green-50 rounded" title="Sauvegarder">
                        {savingCapacity ? <Loader size={11} className="animate-spin" /> : <Save size={11} />}
                      </button>
                      <button onClick={onCancelEditCapacity} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded" title="Annuler">
                        <X size={11} />
                      </button>
                    </>
                  )}
                </span>
              </td>
              {DAYS.map(d => {
                const max = capacity[d.key] || 14;
                return (
                  <td key={d.key} className="text-center px-1 py-2">
                    {editingCapacity ? (
                      <input type="number" min={1} max={30}
                        value={capacityForm[d.key] || 14}
                        onChange={e => setCapacityForm(p => ({ ...p, [d.key]: parseInt(e.target.value, 10) || 14 }))}
                        className="w-10 text-center text-xs border border-gray-300 rounded py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-300" />
                    ) : (
                      <span className="text-xs text-gray-500">{max}</span>
                    )}
                  </td>
                );
              })}
              <td colSpan={canSeePrevisionnel ? 9 : 8} />
            </tr>
            <tr className="bg-gray-50 border-t border-gray-100">
              <td className="px-3 py-2 text-xs text-gray-400" colSpan={2}>Taux d'occupation</td>
              {DAYS.map(d => {
                const count = occupancy[d.key] || 0;
                const max = capacity[d.key] || 14;
                return (
                  <td key={d.key} className="text-center px-1 py-2">
                    <CapacityBar count={count} max={max} />
                  </td>
                );
              })}
              <td colSpan={canSeePrevisionnel ? 9 : 8} />
            </tr>
          </tfoot>
        </table>
      </div>

      {!addingRow && (
        <div className="px-4 py-2 border-t border-gray-100">
          <button onClick={onStartAdd}
            className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 font-medium">
            <Plus size={13} />
            Ajouter un enfant
          </button>
        </div>
      )}

      {/* Vue mensuelle évolution des places (collapsible, tous les utilisateurs) */}
      <div className="px-4 pb-4">
        <MonthlyPlacesEvolution
          contracts={contracts}
          capacity={capacity}
          schoolYear={schoolYear}
        />
      </div>
    </div>
  );
};

// ─── Cellule date avec état local (évite fermeture du calendrier natif) ──────

const DateCell = ({ child, docType, check, onSave, expired }) => {
  const [localDate, setLocalDate] = useState(
    check?.dateValue ? new Date(check.dateValue).toISOString().split('T')[0] : ''
  );

  // Sync si la valeur serveur change (ex: après save d'un autre champ)
  const serverDate = check?.dateValue ? new Date(check.dateValue).toISOString().split('T')[0] : '';
  useEffect(() => { setLocalDate(serverDate); }, [serverDate]);

  const handleBlur = () => {
    if (localDate !== serverDate) {
      onSave(child, docType, localDate);
    }
  };

  return (
    <>
      <input
        type="date"
        value={localDate}
        onChange={e => setLocalDate(e.target.value)}
        onBlur={handleBlur}
        className={`text-xs border rounded px-1.5 py-1 w-[120px] focus:outline-none focus:ring-1 focus:ring-amber-300 ${
          expired ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200'
        }`}
      />
      {check?.validUntil && (
        <span className={`text-[10px] ${expired ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
          {expired ? 'Expiré' : `→ ${new Date(check.validUntil).toLocaleDateString('fr-FR')}`}
        </span>
      )}
    </>
  );
};

// ─── Onglet Suivi Documents Familles ─────────────────────────────────────────

const DocumentsTab = ({ structures, showStructureName, schoolYear, showArchived }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [saving, setSaving] = useState(null); // "childId-typeId"
  const [exporting, setExporting] = useState(false);

  const structureIds = structures.map(s => s.id);
  const structureMap = {};
  structures.forEach(s => { structureMap[s.id] = s.name; });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      if (showStructureName) {
        res = await getFamilyDocumentsMultiStructures({ structureIds: structureIds.join(','), schoolYear });
      } else {
        res = await getFamilyDocuments({ structureId: structureIds[0], schoolYear });
      }
      setChildren(res.data.children || []);
      setDocumentTypes(res.data.documentTypes || []);
    } catch (e) {
      console.error('[Documents] load:', e);
      toast.error('Erreur chargement documents familles');
    } finally {
      setLoading(false);
    }
  }, [structureIds.join(','), schoolYear, showStructureName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Résoudre le check pour un enfant + type selon persistent/schoolYear
  const getCheck = (child, docType) => {
    const checks = child.documentChecks || [];
    if (docType.persistent) {
      // Persistent : chercher schoolYear='permanent' (ou n'importe quel check existant en fallback)
      return checks.find(dc => dc.documentTypeId === docType.id && dc.schoolYear === 'permanent')
        || checks.find(dc => dc.documentTypeId === docType.id);
    }
    // Non-persistent : chercher exactement cette année scolaire
    return checks.find(dc => dc.documentTypeId === docType.id && dc.schoolYear === schoolYear);
  };

  const updateChildChecks = (childId, newCheck) => {
    setChildren(prev => prev.map(c => {
      if (c.id !== childId) return c;
      const checks = [...(c.documentChecks || [])];
      const idx = checks.findIndex(dc => dc.id === newCheck.id);
      if (idx >= 0) checks[idx] = newCheck;
      else checks.push(newCheck);
      return { ...c, documentChecks: checks };
    }));
  };

  const handleToggle = async (child, docType, action) => {
    const key = `${child.id}-${docType.id}`;
    setSaving(key);
    try {
      const existing = getCheck(child, docType);
      let body = { schoolYear };

      if (action === 'na') {
        body = { ...body, notApplicable: !(existing?.notApplicable), checked: false, dateValue: null };
      } else if (docType.fieldType === 'date_validity') {
        return;
      } else {
        if (existing?.notApplicable) {
          body = { ...body, notApplicable: false, checked: true };
        } else {
          body = { ...body, checked: !(existing?.checked), notApplicable: false };
        }
      }

      const res = await updateFamilyDocumentCheck(child.id, docType.id, body);
      updateChildChecks(child.id, res.data);
    } catch (e) {
      console.error('[Documents] toggle:', e);
      toast.error('Erreur mise à jour');
    } finally {
      setSaving(null);
    }
  };

  const handleDateChange = async (child, docType, dateValue) => {
    const key = `${child.id}-${docType.id}`;
    setSaving(key);
    try {
      const body = { checked: !!dateValue, dateValue: dateValue || null, notApplicable: false, schoolYear };
      const res = await updateFamilyDocumentCheck(child.id, docType.id, body);
      updateChildChecks(child.id, res.data);
    } catch (e) {
      console.error('[Documents] date:', e);
      toast.error('Erreur mise à jour date');
    } finally {
      setSaving(null);
    }
  };

  // Filtrer archivés (isArchived vient du backend)
  const visibleChildren = showArchived ? children : children.filter(c => !c.isArchived);
  const archivedCount = children.filter(c => c.isArchived).length;

  // Grouper par structure si multi
  const childrenByStructure = {};
  if (showStructureName) {
    structureIds.forEach(sid => { childrenByStructure[sid] = []; });
    visibleChildren.forEach(c => {
      const sid = c.structureId || c.structure?.id;
      if (childrenByStructure[sid]) childrenByStructure[sid].push(c);
    });
  } else {
    childrenByStructure[structureIds[0]] = visibleChildren;
  }

  // Stats globales (sur les enfants visibles)
  const totalChildren = visibleChildren.length;
  const totalChecks = visibleChildren.reduce((sum, c) =>
    sum + documentTypes.filter(dt => {
      const ck = getCheck(c, dt);
      return ck?.checked || ck?.notApplicable;
    }).length, 0);
  const totalExpected = totalChildren * documentTypes.length;
  const completionPct = totalExpected > 0 ? Math.round((totalChecks / totalExpected) * 100) : 0;

  const now = new Date();
  const isExpired = (check) => check?.validUntil && new Date(check.validUntil) < now;

  // Export PDF paysage — reprend les filtres actifs (schoolYear + structure(s) + archivés)
  const handleExportPdf = async () => {
    if (documentTypes.length === 0 || visibleChildren.length === 0) {
      toast.info("Rien à exporter pour cette période.");
      return;
    }
    setExporting(true);
    try {
      const { generateFamilyDocumentsPdf } = await import('../utils/familyDocumentsPdf');
      generateFamilyDocumentsPdf({
        children,
        documentTypes,
        structures,
        structureName: structures[0]?.name || '',
        schoolYear,
        showStructureName,
        showArchived,
      });
    } catch (e) {
      console.error('[Documents] export PDF:', e);
      toast.error('Erreur génération PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400">
        <Loader size={24} className="animate-spin mr-2" />
        <span>Chargement documents...</span>
      </div>
    );
  }

  if (documentTypes.length === 0) {
    return (
      <div className="text-center py-10 text-gray-400">
        <FileCheck size={28} className="mx-auto mb-2 opacity-40" />
        <p>Aucun type de document configuré.</p>
        <p className="text-xs mt-1">Rendez-vous dans <strong>Administration &gt; Types documents familles</strong> pour en créer.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Enfants" value={totalChildren} sub={`année ${schoolYear?.replace('-', '/20')}${archivedCount > 0 && !showArchived ? ` (+ ${archivedCount} archivés)` : ''}`} icon={Baby} color="amber" />
        <KpiCard label="Documents types" value={documentTypes.length} sub="configurés" icon={FileCheck} color="amber" />
        <KpiCard label="Complétude" value={`${completionPct}%`} sub={`${totalChecks}/${totalExpected}`} icon={CheckCircle} color={completionPct >= 80 ? 'green' : 'amber'} />
        <KpiCard
          label="Validités expirées"
          value={visibleChildren.reduce((sum, c) => sum + documentTypes.filter(dt => isExpired(getCheck(c, dt))).length, 0)}
          sub="à renouveler"
          icon={AlertCircle}
          color="amber"
        />
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-gray-500 flex-1 min-w-[280px]">
          Suivi des documents familles pour l'année <strong>{schoolYear?.replace('-', '/20')}</strong>.
          Types configurables dans <strong>Administration &gt; Types documents familles</strong>.
          <span className="text-gray-400 ml-1">(🔒 = persistant entre années)</span>
        </p>
        <button
          onClick={handleExportPdf}
          disabled={exporting || documentTypes.length === 0 || visibleChildren.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-sm"
          title="Exporter en PDF (année scolaire et structure(s) affichées)"
        >
          {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
          Exporter PDF
        </button>
      </div>

      {/* Tableau par structure */}
      {structureIds.map(sid => {
        const list = childrenByStructure[sid] || [];
        return (
          <div key={sid} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {showStructureName && (
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <Building size={14} className="text-gray-400" />
                <span className="font-semibold text-gray-700 text-sm">{structureMap[sid] || sid}</span>
                <span className="text-xs text-gray-400">({list.length} enfant{list.length > 1 ? 's' : ''})</span>
              </div>
            )}
            {list.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-400 text-sm">Aucun enfant avec contrat actif pour cette période.</div>
            ) : (
              <div className="overflow-auto max-h-[65vh]">
                <table className="min-w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky left-0 z-30 min-w-[150px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                        Enfant
                      </th>
                      {documentTypes.map(dt => (
                        <th key={dt.id} className="px-3 py-3 bg-gray-50 min-w-[100px] align-top text-center">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider leading-tight">{dt.name}</div>
                          {dt.persistent && <div className="text-[9px] text-gray-400 mt-0.5">🔒</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {list.map(child => {
                      const isArch = child.isArchived;
                      return (
                        <tr key={child.id} className={`transition ${isArch ? 'opacity-40 bg-gray-50/50' : 'hover:bg-amber-50/30'}`}>
                          <td className="px-4 py-2.5 font-medium text-gray-800 sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                            <div className="whitespace-nowrap">{getChildName(child)}</div>
                            {child.birthDate && (
                              <div className="text-[11px] text-gray-400">{fmtAge(child.birthDate)}</div>
                            )}
                          </td>
                          {documentTypes.map(dt => {
                            const check = getCheck(child, dt);
                            const isSaving = saving === `${child.id}-${dt.id}`;
                            const expired = isExpired(check);

                            return (
                              <td key={dt.id} className="text-center px-3 py-2.5 align-middle">
                                {isSaving ? (
                                  <div className="h-9 flex items-center justify-center">
                                    <Loader size={14} className="animate-spin text-gray-400" />
                                  </div>
                                ) : dt.fieldType === 'date_validity' ? (
                                  /* Date field */
                                  <div className="flex flex-col items-center gap-0.5">
                                    {check?.notApplicable ? (
                                      <button
                                        onClick={() => handleToggle(child, dt, 'na')}
                                        className="h-9 w-9 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center hover:bg-gray-200 transition"
                                        title="N/A — cliquer pour rétablir"
                                      >
                                        <Minus size={14} />
                                      </button>
                                    ) : (
                                      <DateCell
                                        child={child}
                                        docType={dt}
                                        check={check}
                                        onSave={handleDateChange}
                                        expired={expired}
                                      />
                                    )}
                                    <button
                                      onClick={() => handleToggle(child, dt, 'na')}
                                      className={`text-[10px] leading-none ${check?.notApplicable ? 'text-gray-500 font-medium' : 'text-gray-300 hover:text-gray-500'}`}
                                      title="Non applicable"
                                    >
                                      N/A
                                    </button>
                                  </div>
                                ) : (
                                  /* Checkbox field */
                                  <div className="flex flex-col items-center gap-0.5">
                                    <button
                                      onClick={() => handleToggle(child, dt, 'toggle')}
                                      className={`h-9 w-9 rounded-lg flex items-center justify-center transition ${
                                        check?.notApplicable
                                          ? 'bg-gray-100 text-gray-400'
                                          : check?.checked
                                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                          : 'bg-white border-2 border-gray-200 text-gray-300 hover:border-gray-400'
                                      }`}
                                      title={check?.notApplicable ? 'N/A' : check?.checked ? 'Coché' : 'Non coché'}
                                    >
                                      {check?.notApplicable ? <Minus size={14} /> : check?.checked ? <Check size={16} strokeWidth={2.5} /> : null}
                                    </button>
                                    <button
                                      onClick={() => handleToggle(child, dt, 'na')}
                                      className={`text-[10px] leading-none ${check?.notApplicable ? 'text-gray-500 font-medium' : 'text-gray-300 hover:text-gray-500'}`}
                                      title="Non applicable"
                                    >
                                      N/A
                                    </button>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Onglet Tarifs (lecture seule) ────────────────────────────────────────────

const TariffsReadOnlyTab = ({ tariffs, structures, showStructureName, schoolYear }) => {
  const structureMap = {};
  structures.forEach(s => { structureMap[s.id] = s.name; });

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : null;

  // Bornes de l'année scolaire sélectionnée (ex: "2025-26" → 1er sept 2025 → 31 août 2026)
  const syStart = schoolYear ? parseInt(schoolYear.split('-')[0], 10) : null;
  const periodStart = syStart ? new Date(syStart, 8, 1) : null;     // 1er septembre
  const periodEnd = syStart ? new Date(syStart + 1, 7, 31) : null;  // 31 août

  // Filtrer par période : un tarif est valide si sa plage chevauche l'année scolaire
  const isInPeriod = (t) => {
    if (!periodStart) return true; // pas de filtre
    // Permanent (pas de dates) → toujours valide
    if (!t.validFrom && !t.validUntil) return true;
    // validUntil avant le début de l'année scolaire → hors période
    if (t.validUntil && new Date(t.validUntil) < periodStart) return false;
    // validFrom après la fin de l'année scolaire → hors période
    if (t.validFrom && new Date(t.validFrom) > periodEnd) return false;
    return true;
  };

  // Grouper les tarifs par structure, filtrés par période
  const tariffsByStructure = {};
  const structureIds = showStructureName ? structures.map(s => s.id) : [structures[0]?.id].filter(Boolean);
  structureIds.forEach(sid => { tariffsByStructure[sid] = []; });
  tariffs.forEach(t => {
    if (tariffsByStructure[t.structureId] && isInPeriod(t)) {
      tariffsByStructure[t.structureId].push(t);
    }
  });

  // Trier par montant croissant
  const sortTariffs = (list) => [...list].sort((a, b) => parseFloat(a.monthlyAmount) - parseFloat(b.monthlyAmount));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Tarifs valides pour l'année <strong>{schoolYear?.replace('-', '/20')}</strong>. Pour modifier, rendez-vous dans <strong>Administration &gt; Tarifs crèche</strong>.
        </p>
      </div>

      {structureIds.map(sid => {
        const list = sortTariffs(tariffsByStructure[sid] || []);
        return (
          <div key={sid} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {showStructureName && (
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <Building size={14} className="text-gray-400" />
                <span className="font-semibold text-gray-700 text-sm">{structureMap[sid] || sid}</span>
                <span className="text-xs text-gray-400">({list.length} tarif{list.length > 1 ? 's' : ''})</span>
              </div>
            )}
            {list.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-400 text-sm">Aucun tarif pour cette période.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Nom</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Jours</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-gray-600">Montant mensuel</th>
                      <th className="text-right px-3 py-2.5 font-semibold text-gray-600">Tarif horaire</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Type</th>
                      <th className="text-center px-3 py-2.5 font-semibold text-gray-600">Validité</th>
                      <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(t => (
                      <tr key={t.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                        <td className="px-4 py-2.5 font-medium text-gray-800">{t.name}</td>
                        <td className="text-center px-3 py-2.5">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{t.days}</span>
                        </td>
                        <td className="text-right px-3 py-2.5 font-semibold text-gray-800">{fmtMoney(t.monthlyAmount)}</td>
                        <td className="text-right px-3 py-2.5">
                          {(() => {
                            const hourlyRate = parseFloat(t.monthlyAmount) / 4.33 / t.days / 10.5;
                            const formatted = hourlyRate.toFixed(2);
                            const full = hourlyRate.toFixed(8);
                            return (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-gray-700 font-medium">{formatted} EUR</span>
                                <button
                                  onClick={() => navigator.clipboard.writeText(full)}
                                  className="p-0.5 text-gray-300 hover:text-amber-500 transition"
                                  title={`Copier ${full}`}
                                >
                                  <Copy size={12} />
                                </button>
                              </span>
                            );
                          })()}
                        </td>
                        <td className="text-center px-3 py-2.5">
                          {t.isPublicEmployer ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">PE</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Standard</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2.5">
                          {t.validFrom || t.validUntil ? (
                            <span className="text-xs whitespace-nowrap text-gray-500">
                              {fmtDate(t.validFrom) || '…'} → {fmtDate(t.validUntil) || '…'}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">Permanent</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs max-w-48 truncate">{t.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Onglet CA Prévisionnel ───────────────────────────────────────────────────

const PrevisionnelTab = ({ dataByStructure, structures, showStructureName, schoolYear, showArchived, contractStatuses = CONTRACT_STATUS_FALLBACK }) => {
  const allData = [];
  for (const s of structures) {
    const sd = dataByStructure[s.id];
    if (!sd) continue;
    const filtered = showArchived
      ? sd.contracts
      : sd.contracts.filter(c => c.status !== 'archive');
    for (const c of filtered) {
      allData.push({ ...c, structureName: s.name });
    }
  }

  // Forfait mensuel de base (inchangé, vient du tarif)
  const forfaitMonthly = (c) => c.tariff ? parseFloat(c.tariff.monthlyAmount || 0) : 0;
  // Montant PE négocié : montant ANNUEL saisi sur le contrat, divisé par 12 pour l'équivalent mensuel
  const negotiatedMonthlyEquiv = (c) => {
    if (c.negotiatedAmount == null) return 0;
    const annual = parseFloat(c.negotiatedAmount);
    return isNaN(annual) || annual <= 0 ? 0 : annual / 12;
  };
  // CA mensuel effectif = forfait mensuel + (PE négocié annuel / 12)
  const effectiveMonthly = (c) => forfaitMonthly(c) + negotiatedMonthlyEquiv(c);

  const totalCA = allData.reduce((sum, c) => {
    const monthly = effectiveMonthly(c);
    if (isNaN(monthly) || monthly <= 0) return sum;
    const months = countActiveMonths(c.contractStart, c.contractEnd, schoolYear);
    return sum + (monthly * months);
  }, 0);

  const totalMonthlyEquiv = allData.reduce((sum, c) => {
    const t = effectiveMonthly(c);
    return sum + (isNaN(t) ? 0 : t);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Contrats actifs" value={allData.filter(c => c.status !== 'archive').length} sub="hors archivés" icon={Users} color="amber" />
        <KpiCard label="Validés Meeko" value={allData.filter(c => c.status === 'valide_meeko').length}
          sub={`${allData.filter(c => ['contrat_signe', 'valide_meeko'].includes(c.status)).length} signés ou +`}
          icon={CheckCircle} color="green" />
        <KpiCard label="CA mensuel (plein)" value={fmtMoney(Math.round(totalMonthlyEquiv))} sub="forfait + PE ÷ 12" icon={TrendingUp} color="amber" large />
        <KpiCard label="CA annuel projeté" value={fmtMoney(Math.round(totalCA))} sub="selon début/fin réels" icon={Calendar} color="amber" large />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-700 text-sm">Détail par enfant</h2>
          <span className="text-xs text-gray-400">{allData.length} contrat(s)</span>
        </div>
        {allData.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <TrendingUp size={28} className="mx-auto mb-2 opacity-40" />
            <p>Aucun contrat actif.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {showStructureName && <th className="text-left px-4 py-3 font-semibold text-gray-600">Structure</th>}
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Enfant</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600">Jours</th>
                  <th className="text-left px-3 py-3 font-semibold text-gray-600">Tarif</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600">Période</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600">Mois</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-600" title="Forfait mensuel (tarif)">€/mois</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-600" title="Montant Place Entreprise négocié annuel ÷ 12">€ PE /mois</th>
                  <th className="text-right px-3 py-3 font-semibold text-gray-600" title="(forfait + PE/12) × mois actifs">CA total</th>
                  <th className="text-center px-3 py-3 font-semibold text-gray-600">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allData.map(contract => {
                  const days = DAYS.filter(d => contract[d.key]).length;
                  const tarifAmount = contract.tariff ? parseFloat(contract.tariff.monthlyAmount || 0) : 0;
                  const peMonthly = negotiatedMonthlyEquiv(contract);
                  const usedMonthly = tarifAmount + peMonthly;
                  const months = countActiveMonths(contract.contractStart, contract.contractEnd, schoolYear);
                  const totalContract = isNaN(usedMonthly) ? 0 : usedMonthly * months;
                  const periodLabel = [
                    fmtMonthShort(contract.contractStart) || 'Sept',
                    fmtMonthShort(contract.contractEnd) || 'Août',
                  ].join(' → ');
                  const isArchived = contract.status === 'archive';

                  return (
                    <tr key={contract.id} className={`hover:bg-gray-50 transition-colors ${isArchived ? 'opacity-50' : ''}`}>
                      {showStructureName && <td className="px-4 py-2.5 text-xs text-gray-500">{contract.structureName}</td>}
                      <td className="px-4 py-2.5 font-medium text-gray-800">
                        {contract.child ? getChildName(contract.child) : '—'}
                      </td>
                      <td className="text-center px-3 py-2.5 text-gray-600">{days}j</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{contract.tariff?.name || '—'}</td>
                      <td className="text-center px-3 py-2.5 text-xs text-gray-500">{periodLabel}</td>
                      <td className="text-center px-3 py-2.5 text-xs font-medium text-gray-600">{months}</td>
                      {/* Forfait mensuel (tarif standard, inchangé) */}
                      <td className="text-right px-3 py-2.5 font-medium text-gray-700">
                        {tarifAmount > 0 ? fmtMoney(Math.round(tarifAmount)) : '—'}
                      </td>
                      {/* Montant PE négocié converti au mois (annuel ÷ 12), arrondi à l'entier */}
                      <td className="text-right px-3 py-2.5 font-medium text-amber-700">
                        {peMonthly > 0 ? (
                          <span title={`PE négocié annuel : ${fmtMoney(parseFloat(contract.negotiatedAmount))}`}>
                            +{fmtMoney(Math.round(peMonthly))}
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      {/* CA total sur la période : (forfait + PE/12) × mois actifs, arrondi */}
                      <td className="text-right px-3 py-2.5 font-medium text-amber-700">
                        {totalContract > 0 ? fmtMoney(Math.round(totalContract)) : '—'}
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <StatusBadge status={contract.status} statuses={contractStatuses} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-amber-50 border-t-2 border-amber-200 font-semibold">
                  {showStructureName && <td className="px-4 py-3" />}
                  <td className="px-4 py-3 text-amber-800">Total</td>
                  <td className="text-center px-3 py-3 text-amber-700">{allData.length} contrats</td>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3" />
                  {/* Total forfait mensuel */}
                  <td className="text-right px-3 py-3 text-amber-700">
                    {fmtMoney(Math.round(allData.reduce((s, c) => s + forfaitMonthly(c), 0)))}/mois
                  </td>
                  {/* Total PE mensuel équivalent (annuel ÷ 12) */}
                  <td className="text-right px-3 py-3 text-amber-700">
                    +{fmtMoney(Math.round(allData.reduce((s, c) => s + negotiatedMonthlyEquiv(c), 0)))}/mois
                  </td>
                  {/* CA total sur la période */}
                  <td className="text-right px-3 py-3 text-amber-800">{fmtMoney(Math.round(totalCA))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center">
        CA calculé selon la période de chaque contrat (début/fin). Sans dates = 12 mois complets.
        {!showArchived && ' Les contrats archivés sont exclus.'}
      </p>
    </div>
  );
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, color = 'amber', large = false }) => {
  const colors = {
    amber: { bg: 'bg-amber-50', icon: 'text-amber-500', value: 'text-amber-700' },
    green: { bg: 'bg-green-50', icon: 'text-green-500', value: 'text-green-700' },
  };
  const cfg = colors[color] || colors.amber;
  return (
    <div className="rounded-xl border border-gray-200 p-4 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className={`font-bold ${large ? 'text-xl' : 'text-2xl'} ${cfg.value}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon size={16} className={cfg.icon} />
        </div>
      </div>
    </div>
  );
};

export default CrechePage;
